// Service layer for medical records - no direct Firestore access
// All data access goes through backend APIs

import { getAuthToken } from '../utils/authUtils';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

export class MedicalRecordsService {
  static async getMedicalRecords(limitCount = 1000) {
    try {
      // Use the clinical API for medical records
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/medical-records?limit=${limitCount}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const recordsData = await response.json();
      return recordsData;
    } catch (error) {
      console.error('Error fetching medical records:', error);
      throw error;
    }
  }

  static async getMedicalRecordsByPatient(patientId, limitCount = 100) {
    try {
      // Use the clinical API for patient-specific medical records
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/patients/${patientId}/medical-records?limit=${limitCount}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const recordsData = await response.json();
      return recordsData;
    } catch (error) {
      console.error('Error fetching medical records for patient:', error);
      throw error;
    }
  }

  static async getMedicalRecordsByType(recordType, limitCount = 100) {
    try {
      // Use the clinical API for type-specific medical records
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/medical-records/type/${recordType}?limit=${limitCount}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const recordsData = await response.json();
      return recordsData;
    } catch (error) {
      console.error('Error fetching medical records by type:', error);
      throw error;
    }
  }

  static async createMedicalRecord(recordData) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/medical-records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(recordData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const createdRecord = await response.json();
      return createdRecord;
    } catch (error) {
      console.error('Error creating medical record:', error);
      throw error;
    }
  }

  static async updateMedicalRecord(recordId, updateData) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/medical-records/${recordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedRecord = await response.json();
      return updatedRecord;
    } catch (error) {
      console.error('Error updating medical record:', error);
      throw error;
    }
  }

  static async deleteMedicalRecord(recordId) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/medical-records/${recordId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return { success: true, message: 'Medical record deleted successfully' };
    } catch (error) {
      console.error('Error deleting medical record:', error);
      throw error;
    }
  }

  static async getMedicalRecordById(recordId) {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/medical-records/${recordId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const recordData = await response.json();
      return recordData;
    } catch (error) {
      console.error('Error fetching medical record by ID:', error);
      throw error;
    }
  }

  // Additional methods for enhanced functionality
  static async searchMedicalRecords(searchTerm, filters = {}) {
    try {
      const token = await getAuthToken();
      const queryParams = new URLSearchParams({
        search: searchTerm,
        ...filters
      });

      const response = await fetch(`${API_BASE}/clinical/medical-records/search?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const searchResults = await response.json();
      return searchResults;
    } catch (error) {
      console.error('Error searching medical records:', error);
      throw error;
    }
  }

  static async getMedicalRecordTypes() {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}/clinical/medical-records/types`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const types = await response.json();
      return types;
    } catch (error) {
      console.error('Error fetching medical record types:', error);
      throw error;
    }
  }
}
